import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { api } from "../api";
import { buildItemUrl } from "../utils/itemUrl";
import usePageTitle from "../hooks/usePageTitle";
import { MODERATION_REJECTION_MESSAGE, moderateTextFields } from "../utils/textModeration";

const normalizeCategory = (value) => String(value || "").trim().toLowerCase();

const isFunkoCategory = (category) => {
  const normalized = normalizeCategory(category);
  return normalized.includes("funko");
};

const isCardCategory = (category) => {
  const normalized = normalizeCategory(category);
  return normalized.includes("sports card") || normalized.includes("tcg") || normalized.includes("pokemon");
};

const isComicCategory = (category) => {
  const normalized = normalizeCategory(category);
  return normalized.includes("comic");
};

const isToyCategory = (category) => {
  const normalized = normalizeCategory(category);
  return normalized.includes("lego") || normalized.includes("toy") || normalized.includes("misc");
};

const trimFieldValue = (value) => String(value || "").trim();
const safeText = (value) => (typeof value === "string" ? value : String(value ?? ""));

const hasTwoWords = (value) => trimFieldValue(value).split(/\s+/).filter(Boolean).length >= 2;

const isValidManualYear = (value) => {
  const text = trimFieldValue(value);
  if (!/^\d{4}$/.test(text)) return false;
  const year = Number(text);
  const currentYear = new Date().getFullYear();
  return year >= 1900 && year <= currentYear + 1;
};

const getCategoryRequirementHint = () => {
  return "Category details are optional but help improve pricing and search accuracy.";
};

const hasManualAddImage = (formData, uploadedPreview, uploadedImageName) => {
  return Boolean(
    trimFieldValue(formData.image_url) ||
    trimFieldValue(uploadedPreview) ||
    trimFieldValue(uploadedImageName)
  );
};

const validateManualAddForm = (formData, { uploadedPreview, uploadedImageName } = {}) => {
  const name = trimFieldValue(formData.name);
  const brand = trimFieldValue(formData.brand);
  const category = trimFieldValue(formData.category);
  const yearReleased = trimFieldValue(formData.year_released);
  const itemCondition = trimFieldValue(formData.item_condition);
  const hasImage = hasManualAddImage(formData, uploadedPreview, uploadedImageName);

  if (!hasImage) {
    return { ok: false, message: "Please add an image URL or choose an image before adding this item." };
  }

  if (!hasTwoWords(name)) {
    return { ok: false, message: "Please enter a name with at least 2 words." };
  }

  if (yearReleased && !isValidManualYear(yearReleased)) {
    const currentYear = new Date().getFullYear() + 1;
    return {
      ok: false,
      message: `Please enter a valid 4-digit year between 1900 and ${currentYear}.`,
    };
  }

  const missingBasicFields = [];
  if (!brand) missingBasicFields.push("Brand");
  if (!category) missingBasicFields.push("Category");
  if (!yearReleased) missingBasicFields.push("Year Released");
  if (!itemCondition) missingBasicFields.push("Condition");

  if (missingBasicFields.length) {
    return {
      ok: false,
      message: `Please complete all required fields before adding this item: ${missingBasicFields.join(", ")}.`,
    };
  }

  return { ok: true, message: "" };
};

const renderField = (field, formData, handleChange) => {
  if (field.type === "select") {
    return (
      <FormControl fullWidth size="small">
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {field.label}
          {field.required ? " *" : ""}
        </Typography>
        <Select
          value={formData[field.name] || field.defaultValue || ""}
          onChange={handleChange(field.name)}
          required={Boolean(field.required)}
        >
          {(field.options || []).map((option) => (
            <MenuItem key={`${field.name}-${option}`} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label={field.label}
      value={formData[field.name] ?? ""}
      onChange={handleChange(field.name)}
      fullWidth
      size="small"
      required={Boolean(field.required)}
      type={field.type || "text"}
      placeholder={field.placeholder || ""}
      multiline={Boolean(field.multiline)}
      rows={field.rows || undefined}
      InputLabelProps={field.type === "number" ? { shrink: true } : undefined}
    />
  );
};

const getCategoryDetailFields = (category) => {
  if (isFunkoCategory(category)) {
    return [
      { name: "character_name", label: "Character Name", placeholder: "Batman" },
      { name: "series_collection", label: "Series / Collection", placeholder: "DC Heroes" },
      { name: "number", label: "Number", placeholder: "45" },
      { name: "edition_variant", label: "Edition Variant", placeholder: "Hot Topic" },
      { name: "exclusive_name", label: "Exclusive Name", placeholder: "Hot Topic Exclusive" },
      { name: "sticker_type", label: "Sticker Type", placeholder: "Shared Convention" },
      { name: "size_variant", label: "Size Variant", placeholder: "10 inch" },
    ];
  }
  if (isCardCategory(category)) {
    return [
      { name: "player", label: "Player", placeholder: "Connor McDavid" },
      { name: "team", label: "Team", placeholder: "Edmonton Oilers" },
      { name: "set_name", label: "Set Name", placeholder: "Upper Deck Series 2" },
      { name: "language", label: "Language", placeholder: "English" },
      { name: "number", label: "Number", placeholder: "451" },
      { name: "rarity", label: "Rarity", placeholder: "Rare / Holo / SSP" },
      { name: "edition_variant", label: "Edition Variant", placeholder: "Young Guns" },
      { name: "authentication", label: "Authentication", placeholder: "PSA / Beckett / Raw" },
      { name: "inscription", label: "Inscription", placeholder: "Auto / On-card" },
      { name: "secret_rarity", label: "Secret Rarity", placeholder: "Alt Art / Secret Rare" },
    ];
  }
  if (isComicCategory(category)) {
    return [
      { name: "publisher", label: "Publisher", placeholder: "DC Comics" },
      { name: "issue_number", label: "Issue Number", placeholder: "1" },
      { name: "volume", label: "Volume", placeholder: "Vol. 3" },
      { name: "cover_variant", label: "Cover Variant", placeholder: "Cover B" },
      { name: "print_edition", label: "Print Edition", placeholder: "First Print" },
      { name: "artist", label: "Artist", placeholder: "Jim Lee" },
    ];
  }
  if (isToyCategory(category)) {
    return [
      { name: "theme", label: "Theme", placeholder: "Star Wars" },
      { name: "piece_count", label: "Piece Count", type: "number", placeholder: "753" },
      { name: "minifigure_count", label: "Minifigure Count", type: "number", placeholder: "6" },
      { name: "platform", label: "Platform", placeholder: "Nintendo Switch" },
      { name: "region", label: "Region", placeholder: "US" },
    ];
  }
  return [
    { name: "person_name", label: "Person Name", placeholder: "Stan Lee" },
    { name: "item_type", label: "Item Type", placeholder: "Signed Poster" },
    { name: "theme", label: "Theme", placeholder: "Marvel" },
    { name: "platform", label: "Platform", placeholder: "PS5" },
    { name: "region", label: "Region", placeholder: "North America" },
  ];
};

const advancedFields = [
  { name: "person_name", label: "Person Name", placeholder: "Stan Lee" },
  { name: "item_type", label: "Item Type", placeholder: "Signed Poster" },
  { name: "authentication", label: "Authentication", placeholder: "PSA / JSA / Beckett" },
  { name: "inscription", label: "Inscription", placeholder: "To Chris" },
  { name: "theme", label: "Theme", placeholder: "Marvel" },
  { name: "platform", label: "Platform", placeholder: "Nintendo Switch" },
  { name: "region", label: "Region", placeholder: "US" },
  { name: "language", label: "Language", placeholder: "English" },
  { name: "publisher", label: "Publisher", placeholder: "Marvel" },
  { name: "issue_number", label: "Issue Number", placeholder: "1" },
  { name: "volume", label: "Volume", placeholder: "Vol. 2" },
  { name: "cover_variant", label: "Cover Variant", placeholder: "Cover A" },
  { name: "print_edition", label: "Print Edition", placeholder: "First Print" },
  { name: "artist", label: "Artist", placeholder: "Alex Ross" },
  { name: "piece_count", label: "Piece Count", type: "number", placeholder: "1245" },
  { name: "minifigure_count", label: "Minifigure Count", type: "number", placeholder: "8" },
  { name: "exclusive_name", label: "Exclusive Name", placeholder: "Hot Topic Exclusive" },
  { name: "sticker_type", label: "Sticker Type", placeholder: "Special Edition Sticker" },
  { name: "size_variant", label: "Size Variant", placeholder: "Jumbo" },
  { name: "secret_rarity", label: "Secret Rarity", placeholder: "Secret Rare" },
];

export default function AddItem() {
  usePageTitle("Add Item");
  const { search } = useLocation();
  const navigate = useNavigate();
  const defaultQuery = new URLSearchParams(search).get("query") || "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [prefillNotice, setPrefillNotice] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadedPreview, setUploadedPreview] = useState("");
  const [uploadedImageName, setUploadedImageName] = useState("");
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [formError, setFormError] = useState("");

  const [formData, setFormData] = useState({
    name: defaultQuery,
    brand: "",
    category: "",
    year_released: "",
    team: "",
    person_name: "",
    item_type: "",
    authentication: "",
    inscription: "",
    player: "",
    character_name: "",
    set_name: "",
    series_collection: "",
    publisher: "",
    issue_number: "",
    volume: "",
    cover_variant: "",
    print_edition: "",
    artist: "",
    theme: "",
    piece_count: "",
    minifigure_count: "",
    platform: "",
    region: "",
    language: "",
    exclusive_name: "",
    sticker_type: "",
    size_variant: "",
    secret_rarity: "",
    number: "",
    rarity: "",
    edition_variant: "",
    is_signed: false,
    item_condition: "New",
    image_url: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const q = (defaultQuery || "").trim();
    if (!q) return;
    let cancelled = false;

    (async () => {
      try {
        const moderationResult = moderateTextFields({ query: q });
        if (!moderationResult.ok) {
          setFormError(MODERATION_REJECTION_MESSAGE);
          return;
        }
        setPrefilling(true);
        const { data } = await api.get(`/api/search?query=${encodeURIComponent(q)}`);

        const first =
          Array.isArray(data) ? data[0] :
          Array.isArray(data?.results) ? data.results[0] :
          data?.items?.[0];

        if (!first || cancelled) return;

        setFormData((f) => ({
          ...f,
          name: first.name || f.name || q,
          brand: first.brand ?? f.brand,
          category: first.category ?? f.category,
          year_released: first.year_released ?? first.year ?? f.year_released,
          team: first.team ?? f.team,
          person_name: first.person_name ?? f.person_name,
          item_type: first.item_type ?? f.item_type,
          authentication: first.authentication ?? f.authentication,
          inscription: first.inscription ?? f.inscription,
          player: first.player ?? f.player,
          character_name: first.character_name ?? first.character ?? f.character_name,
          set_name: first.set_name ?? f.set_name,
          language: first.language ?? f.language,
          series_collection: first.series_collection ?? first.series ?? f.series_collection,
          publisher: first.publisher ?? f.publisher,
          issue_number: first.issue_number ?? f.issue_number,
          volume: first.volume ?? f.volume,
          cover_variant: first.cover_variant ?? f.cover_variant,
          print_edition: first.print_edition ?? f.print_edition,
          artist: first.artist ?? f.artist,
          theme: first.theme ?? f.theme,
          piece_count: first.piece_count ?? f.piece_count,
          minifigure_count: first.minifigure_count ?? f.minifigure_count,
          platform: first.platform ?? f.platform,
          region: first.region ?? f.region,
          exclusive_name: first.exclusive_name ?? f.exclusive_name,
          sticker_type: first.sticker_type ?? f.sticker_type,
          size_variant: first.size_variant ?? f.size_variant,
          secret_rarity: first.secret_rarity ?? f.secret_rarity,
          number: first.number ?? f.number,
          rarity: first.rarity ?? f.rarity,
          edition_variant: first.edition_variant ?? first.variant ?? f.edition_variant,
          is_signed: !!(first.signed ?? first.is_signed ?? f.is_signed),
          item_condition: first.item_condition ?? first.condition ?? f.item_condition,
          image_url: first.image_url ?? first.image ?? f.image_url,
        }));
        setPrefillNotice(true);
        setFormError("");
      } catch {
        // Manual entry still works if prefill fails.
      } finally {
        if (!cancelled) setPrefilling(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [defaultQuery]);

  const categoryDetailFields = useMemo(
    () => (formData.category ? getCategoryDetailFields(formData.category) : []),
    [formData.category]
  );

  const basicSubmitDisabled =
    loading ||
    !trimFieldValue(formData.name) ||
    !trimFieldValue(formData.brand) ||
    !trimFieldValue(formData.category) ||
    !trimFieldValue(formData.year_released) ||
    !trimFieldValue(formData.item_condition) ||
    !hasManualAddImage(formData, uploadedPreview, uploadedImageName);

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setUploadedPreview("");
      setUploadedImageName("");
      setUploadedImageFile(null);
      return;
    }
    setUploadedImageFile(file);
    setUploadedImageName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedPreview(String(reader.result || ""));
    };
    reader.readAsDataURL(file);
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      if (!file) {
        resolve("");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Unable to read the selected image."));
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedUploadedPreview = uploadedPreview || (uploadedImageFile ? await readFileAsDataUrl(uploadedImageFile).catch(() => "") : "");
    const validation = validateManualAddForm(formData, {
      uploadedPreview: resolvedUploadedPreview,
      uploadedImageName: resolvedUploadedPreview ? uploadedImageName : "",
    });
    if (!validation.ok) {
      const message = safeText(validation.message);
      setFormError(message);
      alert(message);
      return;
    }

    const moderationResult = moderateTextFields({
      name: formData.name,
      brand: formData.brand,
      category: formData.category,
      year_released: formData.year_released,
      team: formData.team,
      person_name: formData.person_name,
      item_type: formData.item_type,
      authentication: formData.authentication,
      inscription: formData.inscription,
      player: formData.player,
      character_name: formData.character_name,
      set_name: formData.set_name,
      language: formData.language,
      series_collection: formData.series_collection,
      publisher: formData.publisher,
      issue_number: formData.issue_number,
      volume: formData.volume,
      cover_variant: formData.cover_variant,
      print_edition: formData.print_edition,
      artist: formData.artist,
      theme: formData.theme,
      platform: formData.platform,
      region: formData.region,
      exclusive_name: formData.exclusive_name,
      sticker_type: formData.sticker_type,
      size_variant: formData.size_variant,
      secret_rarity: formData.secret_rarity,
      number: formData.number,
      rarity: formData.rarity,
      edition_variant: formData.edition_variant,
      item_condition: formData.item_condition,
    });
    if (!moderationResult.ok) {
      // Frontend moderation: block inappropriate manual item submissions.
      setFormError(MODERATION_REJECTION_MESSAGE);
      alert(MODERATION_REJECTION_MESSAGE);
      return;
    }

    setFormError("");
    const trimmedName = String(formData.name || "").trim();
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([, value]) => value !== "")
      );
      if (resolvedUploadedPreview) {
        payload.selected_image_data_url = resolvedUploadedPreview;
      }
      if (resolvedUploadedPreview && uploadedImageName) {
        payload.selected_image_name = uploadedImageName;
      }
      const { data } = await api.post("/api/manual-add", payload, {
        headers: { "Content-Type": "application/json" },
      });

      alert(`Item added.\n\nName: ${trimmedName}\nRedirecting to item page...`);
      navigate(buildItemUrl({ item_id: data.item_id, name: trimmedName }));
    } catch (err) {
      console.error("AddItem error:", err);
      const backend = err?.response?.data;
      const msg =
        safeText(backend?.message || backend?.error) ||
        (Array.isArray(backend?.missingFields)
          ? `Missing fields: ${backend.missingFields.join(", ")}`
          : "") ||
        err.message ||
        "There was an error adding your item.";
      alert(safeText(msg));
    } finally {
      setLoading(false);
    }
  };

  const previewSrc = uploadedPreview || formData.image_url;

  return (
    <Box>
      <Box sx={{ maxWidth: 900, mx: "auto", px: isMobile ? 2 : 0, mt: isMobile ? 2 : 4, pb: 8 }}>
        <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom>
            Manually Add New Item
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Only basic fields are required. Category details are optional but help improve pricing and search accuracy.
          </Typography>

          {prefilling && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Looking up details for "{defaultQuery}"...
            </Alert>
          )}
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {safeText(formError)}
            </Alert>
          )}
          {prefillNotice && !prefilling && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPrefillNotice(false)}>
              Filled fields using search results. Review and edit as needed.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Basic Info
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Name"
                      value={formData.name}
                      onChange={handleChange("name")}
                      fullWidth
                      required
                      placeholder="Batman Arkham Knight Funko Pop"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Brand" value={formData.brand} onChange={handleChange("brand")} fullWidth required />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Category"
                      value={formData.category}
                      onChange={handleChange("category")}
                      fullWidth
                      required
                      placeholder="Funko Pop, Sports Card, Comic, LEGO..."
                      helperText="Used to show category-specific fields below."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Year Released"
                          type="number"
                          value={formData.year_released || ""}
                          onChange={handleChange("year_released")}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          sx={{ "& .MuiInputBase-root": { height: 56 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Condition"
                          value={formData.item_condition || "New"}
                          onChange={handleChange("item_condition")}
                          fullWidth
                          required
                          sx={{ "& .MuiInputBase-root": { height: 56 } }}
                        >
                          {["New", "Like New", "Good", "Fair", "Poor"].map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox checked={formData.is_signed} onChange={handleChange("is_signed")} />}
                      label="Signed"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {formData.category ? (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Category Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {getCategoryRequirementHint(formData.category)}
                  </Typography>
                  <Grid container spacing={2}>
                    {categoryDetailFields.map((field) => (
                      <Grid item xs={12} sm={6} key={field.name}>
                        {renderField(field, formData, handleChange)}
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Image
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button variant="outlined" component="label" fullWidth sx={{ minHeight: 56 }}>
                      {uploadedImageName ? `Selected: ${uploadedImageName}` : "Choose Image"}
                      <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Image URL (or choose image)"
                      value={formData.image_url}
                      onChange={handleChange("image_url")}
                      fullWidth
                      required={false}
                      placeholder="https://..."
                      helperText="Optional when you choose an uploaded image."
                    />
                  </Grid>
                  {previewSrc ? (
                    <Grid item xs={12}>
                      <Box
                        component="img"
                        src={previewSrc}
                        alt="preview"
                        sx={{
                          width: "100%",
                          maxHeight: 260,
                          objectFit: "contain",
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: "#fff",
                          p: 1,
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6">Advanced Metadata</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Optional fields for edge cases and richer catalog data.
                    </Typography>
                  </Box>
                  <Button variant="text" onClick={() => setShowAdvanced((open) => !open)}>
                    {showAdvanced ? "Hide Advanced Fields" : "Show Advanced Fields"}
                  </Button>
                </Box>
                <Collapse in={showAdvanced}>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {advancedFields.map((field) => (
                      <Grid item xs={12} sm={6} key={field.name}>
                        {renderField(field, formData, handleChange)}
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </Paper>

              <Box sx={{ mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={basicSubmitDisabled}
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Add Item"}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}
